import React from 'react'

import * as c from '../../../common'
import * as r from '../../../react-utils'

import Icon from 'react-native-vector-icons/MaterialCommunityIcons'
import { useNavigate } from 'react-router-dom'

import Dialog from '../../comps/Dialog/Dialog'
import { auth, createUserAccount } from '../../fire'
import { applyLeadFeeChanges, LeadFeeChange } from './leadFee'
//@ts-ignore
import styles from './AdminEnv.module.css'

// #region constants

const LEAD_FEE_KEY = 'stateToLeadFee'

const COMPLEX_KEYS: readonly string[] = ['contractCompanies', 'loanCompanies']

const estimateSize = (val: c.DBVal): number => {
  if (val === undefined || val === null) return 0
  return new Blob([JSON.stringify(val)]).size
}

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes}B`
  return `${Math.round(bytes / 1024)}KB`
}

const isObject = (val: c.DBVal): boolean =>
  typeof val === 'object' && val !== null && !Array.isArray(val)

const formatValue = (val: c.DBVal): string => {
  if (val === undefined || val === null) return ''
  if (typeof val === 'object') return JSON.stringify(val, null, 2)
  return String(val)
}

const summarizeValue = (key: string, val: c.DBVal): string => {
  if (val === undefined || val === null) return '—'
  if (Array.isArray(val)) return `Array(${val.length})`
  if (isObject(val)) {
    const keys = Object.keys(val)
    if (key === 'contractCompanies') {
      return `ContractCompanies(${keys.length} companies)`
    }
    if (key === 'loanCompanies') {
      return `LoanCompanies(${keys.length} companies)`
    }
    return `Object(${keys.length} keys)`
  }
  const str = String(val)
  return str.length > 60 ? str.slice(0, 60) + '…' : str
}

// #endregion constants

// #region types

interface EnvPendingChange {
  readonly key: string
  readonly oldValue: c.DBVal
  readonly newValue: string
}

type Tab = 'env' | 'people'

// #endregion types

export default React.memo(function AdminPanel() {
  const navigate = useNavigate()
  const admins = r.useEnv<c.Dict<boolean>>('admins')
  const isAdmin = !!admins?.[auth.currentUser?.uid ?? '']

  const [activeTab, setActiveTab] = React.useState<Tab>('env')

  if (!isAdmin) return null

  return (
    <div className={styles['root']}>
      <div className={styles['back-btn']} onClick={() => navigate(-1)}>
        <Icon color="rgba(128,128,128,0.8)" name="arrow-left" size={20} />
        <span>Back</span>
      </div>

      <div className={styles['tab-bar']}>
        <button
          className={
            activeTab === 'env' ? styles['tab-btn-active'] : styles['tab-btn']
          }
          onClick={() => setActiveTab('env')}
        >
          <Icon name="cog-outline" size={18} />
          Env Variables
        </button>
        <button
          className={
            activeTab === 'people'
              ? styles['tab-btn-active']
              : styles['tab-btn']
          }
          onClick={() => setActiveTab('people')}
        >
          <Icon name="account-group-outline" size={18} />
          Closers &amp; Setters
        </button>
      </div>

      {activeTab === 'env' && <EnvVariablesTab />}
      {activeTab === 'people' && <CloserSetterTab />}
    </div>
  )
})

// #region EnvVariablesTab

const EnvVariablesTab = React.memo(function EnvVariablesTab() {
  const allEnv = c.useSelector(c.selectAllEnv)

  const [filter, setFilter] = React.useState('')
  const [expandedKey, setExpandedKey] = React.useState<string | null>(null)
  const [editingCell, setEditingCell] = React.useState<string | null>(null)
  const [editValue, setEditValue] = React.useState('')
  const [pendingChanges, setPendingChanges] = React.useState<
    Record<string, EnvPendingChange>
  >({})
  const [showDialog, setShowDialog] = React.useState(false)
  const [applying, setApplying] = React.useState(false)
  const [copiedKey, setCopiedKey] = React.useState<string | null>(null)
  const cancelEditRef = React.useRef(false)

  const envKeys = React.useMemo(() => {
    const keys = Object.keys(allEnv).sort()
    if (!filter) return keys
    const lower = filter.toLowerCase()
    return keys.filter((k) => k.toLowerCase().includes(lower))
  }, [allEnv, filter])

  const leadFee = React.useMemo(() => {
    const val = allEnv[LEAD_FEE_KEY]
    return isObject(val) ? (val as c.Dict<number>) : null
  }, [allEnv])

  const leadFeeStates = React.useMemo(() => {
    if (!leadFee) return []
    return Object.keys(leadFee).sort()
  }, [leadFee])

  const handleCopy = React.useCallback(async (key: string, val: c.DBVal) => {
    try {
      await navigator.clipboard.writeText(formatValue(val))
      setCopiedKey(key)
      setTimeout(() => setCopiedKey(null), 2000)
    } catch {
      // fallback
      const ta = document.createElement('textarea')
      ta.value = formatValue(val)
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
      setCopiedKey(key)
      setTimeout(() => setCopiedKey(null), 2000)
    }
  }, [])

  // #region env editing

  const handleCellClick = React.useCallback(
    (key: string, currentValue: c.DBVal) => {
      setEditingCell(key)
      const existing = pendingChanges[key]
      if (existing) {
        setEditValue(existing.newValue)
      } else {
        setEditValue(formatValue(currentValue))
      }
    },
    [pendingChanges],
  )

  const handleCellBlur = React.useCallback(
    (key: string, currentValue: c.DBVal) => {
      if (cancelEditRef.current) {
        cancelEditRef.current = false
        setEditingCell(null)
        return
      }

      const trimmed = editValue.trim()
      const currentStr = formatValue(currentValue)

      if (trimmed !== '' && trimmed !== currentStr) {
        setPendingChanges((prev) => ({
          ...prev,
          [key]: { key, oldValue: currentValue, newValue: trimmed },
        }))
      } else if (trimmed === currentStr || trimmed === '') {
        setPendingChanges((prev) => {
          const next = { ...prev }
          delete next[key]
          return next
        })
      }
      setEditingCell(null)
    },
    [editValue],
  )

  const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      ;(e.target as HTMLInputElement | HTMLTextAreaElement).blur()
    }
    if (e.key === 'Escape') {
      cancelEditRef.current = true
      ;(e.target as HTMLInputElement | HTMLTextAreaElement).blur()
    }
  }, [])

  const handleLeadFeeClick = React.useCallback(
    (state: string, amount: number) => {
      const cellKey = `${LEAD_FEE_KEY}.${state}`
      setEditingCell(cellKey)
      const existing = pendingChanges[cellKey]
      if (existing) {
        setEditValue(existing.newValue)
      } else {
        setEditValue(String(amount))
      }
    },
    [pendingChanges],
  )

  const handleLeadFeeBlur = React.useCallback(
    (state: string, amount: number) => {
      if (cancelEditRef.current) {
        cancelEditRef.current = false
        setEditingCell(null)
        return
      }

      const cellKey = `${LEAD_FEE_KEY}.${state}`
      const trimmed = editValue.trim()
      const currentStr = String(amount)

      if (trimmed !== '' && trimmed !== currentStr) {
        const parsed = parseFloat(trimmed)
        if (!isNaN(parsed)) {
          setPendingChanges((prev) => ({
            ...prev,
            [cellKey]: { key: cellKey, oldValue: amount, newValue: trimmed },
          }))
        }
      } else if (trimmed === currentStr || trimmed === '') {
        setPendingChanges((prev) => {
          const next = { ...prev }
          delete next[cellKey]
          return next
        })
      }
      setEditingCell(null)
    },
    [editValue],
  )

  // #endregion env editing

  // #region apply

  const changesList = React.useMemo(
    () => Object.values(pendingChanges),
    [pendingChanges],
  )

  const totalChanges = changesList.length
  const hasChanges = totalChanges > 0

  const handleApply = React.useCallback(() => {
    setShowDialog(true)
  }, [])

  // Global switch of the "Other providers" comparison (a missing key is on).
  const comparisonEnabled = c.comparisonFeatureEnabled(
    allEnv[c.OTHER_PROVIDER_COMPARISON_ENV_KEY],
  )

  const handleComparisonToggle = React.useCallback((next: boolean) => {
    const proceed = window.confirm(
      `Turn the "Other providers" comparison ${
        next ? 'ON' : 'OFF'
      } for EVERY customer and rep? This writes ${
        c.OTHER_PROVIDER_COMPARISON_ENV_KEY
      } in Firebase.`,
    )
    if (!proceed) return
    c.dispatch(
      c.writeEnvKey({
        key: c.OTHER_PROVIDER_COMPARISON_ENV_KEY,
        value: next,
      }),
    )
  }, [])

  const faceCameraEnabled = c.signingFaceCameraEnabled(
    allEnv[c.SIGNING_FACE_CAMERA_ENV_KEY],
  )

  const handleFaceCameraToggle = React.useCallback((next: boolean) => {
    const proceed = window.confirm(
      `Turn the signing face camera ${
        next ? 'ON' : 'OFF'
      } for EVERY rep? Screen recording and the audio choice remain available.`,
    )
    if (!proceed) return
    c.dispatch(
      c.writeEnvKey({ key: c.SIGNING_FACE_CAMERA_ENV_KEY, value: next }),
    )
  }, [])

  const handleFinalApply = React.useCallback(() => {
    setApplying(true)

    // Lead-fee edits are folded into ONE write: `writeEnvKey` replaces the
    // whole `stateToLeadFee` child (see `applyLeadFeeChanges`).
    const leadFeeChanges: LeadFeeChange[] = []

    for (const change of changesList) {
      const { key, newValue } = change

      // Extra confirmation for complex keys
      const isComplex = COMPLEX_KEYS.some((k) => key.startsWith(k))
      const size = new Blob([newValue]).size
      if (isComplex || size > 10000) {
        const proceed = window.confirm(
          `⚠️ ${key} is ${formatSize(
            size,
          )}. This will overwrite the value in Firebase.\n\nAre you sure?`,
        )
        if (!proceed) continue
      }

      if (key.startsWith(`${LEAD_FEE_KEY}.`)) {
        const state = key.replace(`${LEAD_FEE_KEY}.`, '')
        leadFeeChanges.push({ state, newValue })
      } else {
        let value: c.DBVal = newValue
        try {
          value = JSON.parse(newValue)
        } catch {
          // keep as string
        }
        c.dispatch(c.writeEnvKey({ key, value }))
      }
    }

    const nextLeadFee = applyLeadFeeChanges(leadFee, leadFeeChanges)
    if (nextLeadFee) {
      c.dispatch(c.writeEnvKey({ key: LEAD_FEE_KEY, value: nextLeadFee }))
    }

    setTimeout(() => {
      setPendingChanges({})
      setShowDialog(false)
      setApplying(false)
    }, 1500)
  }, [changesList, leadFee])

  // #endregion apply

  return (
    <>
      {/* Lead Fee Card */}
      {leadFee && (
        <div className={styles['card']}>
          <div className={styles['card-title']}>Lead Fee by State</div>
          <div className={styles['lead-fee-grid']}>
            {leadFeeStates.map((state) => {
              const amount = leadFee[state] ?? 0
              const cellKey = `${LEAD_FEE_KEY}.${state}`
              const isEditing = editingCell === cellKey
              const change = pendingChanges[cellKey]

              return (
                <div className={styles['lead-fee-item']} key={state}>
                  <span className={styles['lead-fee-state']}>{state}</span>
                  {isEditing ? (
                    <input
                      autoFocus
                      className={styles['value-input']}
                      onBlur={() => handleLeadFeeBlur(state, amount)}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={handleKeyDown}
                      style={{ width: '80px', minHeight: '28px' }}
                      type="text"
                      value={editValue}
                    />
                  ) : (
                    <span
                      className={
                        change
                          ? styles['value-changed']
                          : styles['lead-fee-amount']
                      }
                      onClick={() => handleLeadFeeClick(state, amount)}
                    >
                      {change ? `$${change.newValue}` : `$${amount}`}
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Other providers comparison switch */}
      <div className={styles['card']}>
        <div className={styles['card-title']}>Other providers comparison</div>
        <label>
          <input
            checked={comparisonEnabled}
            onChange={(e) => handleComparisonToggle(e.target.checked)}
            type="checkbox"
          />{' '}
          Enabled for everyone (web page and app tabs)
        </label>
      </div>

      {/* 13331166589: independent of screen and microphone recording. */}
      <div className={styles['card']}>
        <div className={styles['card-title']}>Signing face camera</div>
        <label>
          <input
            checked={faceCameraEnabled}
            onChange={(e) => handleFaceCameraToggle(e.target.checked)}
            type="checkbox"
          />{' '}
          Include the customer camera when recording a signing
        </label>
      </div>

      {/* All Env Variables */}
      <div className={styles['section-header']}>
        <span className={styles['section-title']}>
          All Environment Variables
        </span>
        <span className={styles['section-count']}>{envKeys.length} keys</span>
      </div>

      <div className={styles['filter-bar']}>
        <input
          className={styles['filter-input']}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter keys..."
          type="text"
          value={filter}
        />
      </div>

      {hasChanges && !showDialog && (
        <div className={styles['sticky-bar']}>
          <span className={styles['changes-text']}>
            {totalChanges} unsaved change{totalChanges !== 1 ? 's' : ''}
          </span>
          <button
            className={styles['apply-btn']}
            disabled={applying}
            onClick={handleApply}
          >
            {applying ? 'Applying...' : 'Review & Apply'}
          </button>
        </div>
      )}

      {envKeys.length > 0 ? (
        <div className={styles['table-wrapper']}>
          <table className={styles['table']}>
            <thead>
              <tr>
                <th className={styles['th']}>Key</th>
                <th className={styles['th']}>Value</th>
                <th className={styles['th']} style={{ width: '60px' }}>
                  Size
                </th>
                <th className={styles['th']} style={{ width: '40px' }}>
                  Copy
                </th>
              </tr>
            </thead>
            <tbody>
              {envKeys.map((key) => {
                const value = allEnv[key]
                const isEditing = editingCell === key
                const change = pendingChanges[key]
                const isComplex = COMPLEX_KEYS.includes(key)
                const isExpanded = expandedKey === key
                const size = estimateSize(value)
                const isCopied = copiedKey === key

                return (
                  <React.Fragment key={key}>
                    <tr>
                      <td
                        className={`${styles['td']} ${styles['td-key']}`}
                        onClick={() => {
                          if (isComplex) {
                            setExpandedKey(isExpanded ? null : key)
                          }
                        }}
                      >
                        {isComplex && (
                          <Icon
                            color="rgba(128,128,128,0.8)"
                            name={isExpanded ? 'chevron-down' : 'chevron-right'}
                            size={16}
                            style={{ marginRight: 4 }}
                          />
                        )}
                        {key}
                        {isComplex && (
                          <span className={styles['badge-complex']}>
                            complex
                          </span>
                        )}
                      </td>
                      <td className={`${styles['td']} ${styles['td-value']}`}>
                        {isEditing && !isComplex ? (
                          <textarea
                            autoFocus
                            className={styles['value-input']}
                            onBlur={() => handleCellBlur(key, value)}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            rows={Math.min(5, editValue.split('\n').length)}
                            value={editValue}
                          />
                        ) : (
                          <div
                            className={
                              change
                                ? styles['value-changed']
                                : styles['value-display']
                            }
                            onClick={() => {
                              if (!isComplex) handleCellClick(key, value)
                            }}
                          >
                            <span>
                              {change
                                ? change.newValue
                                : summarizeValue(key, value)}
                            </span>
                            {!isComplex && (
                              <span className={styles['pencil']}>
                                <Icon
                                  color="rgba(128,128,128,0.5)"
                                  name="pencil"
                                  size={12}
                                />
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className={`${styles['td']} ${styles['td-size']}`}>
                        {formatSize(size)}
                      </td>
                      <td className={`${styles['td']} ${styles['td-copy']}`}>
                        <div
                          className={styles['copy-btn']}
                          onClick={() => handleCopy(key, value)}
                          title="Copy to clipboard"
                        >
                          <Icon
                            color={
                              isCopied
                                ? 'rgba(34,197,94,0.9)'
                                : 'rgba(128,128,128,0.5)'
                            }
                            name={isCopied ? 'check' : 'content-copy'}
                            size={16}
                          />
                        </div>
                      </td>
                    </tr>
                    {isComplex && isExpanded && (
                      <tr>
                        <td colSpan={4} className={styles['expanded-cell']}>
                          <div className={styles['expanded-content']}>
                            <div className={styles['expanded-actions']}>
                              <button
                                className={styles['copy-btn-inline']}
                                onClick={() => handleCopy(key, value)}
                              >
                                <Icon
                                  color="rgba(128,128,128,0.8)"
                                  name="content-copy"
                                  size={14}
                                />
                                {isCopied ? 'Copied!' : 'Copy JSON'}
                              </button>
                            </div>
                            <pre className={styles['json-preview']}>
                              {formatValue(value)}
                            </pre>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={styles['empty-state']}>
          No environment variables found
        </div>
      )}

      <Dialog
        onClose={() => setShowDialog(false)}
        open={showDialog}
        title="Review Changes"
      >
        <div className={styles['dialog-content']}>
          <table className={styles['summary-table']}>
            <thead>
              <tr>
                <th>Key</th>
                <th>Before</th>
                <th>After</th>
              </tr>
            </thead>
            <tbody>
              {changesList.map((ch, i) => {
                const displayKey = ch.key.includes('.')
                  ? ch.key.replace('.', ' → ')
                  : ch.key

                return (
                  <tr key={i}>
                    <td>{displayKey}</td>
                    <td className={styles['old-value']}>
                      {formatValue(ch.oldValue).slice(0, 100) || '—'}
                    </td>
                    <td className={styles['new-value']}>
                      {ch.newValue.slice(0, 100)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          <div className={styles['dialog-actions']}>
            <button
              className={styles['apply-btn']}
              disabled={applying}
              onClick={handleFinalApply}
            >
              {applying ? 'Applying...' : 'Apply'}
            </button>
          </div>
        </div>
      </Dialog>
    </>
  )
})

// #endregion EnvVariablesTab

// #region CloserSetterTab

const CloserSetterTab = React.memo(function CloserSetterTab() {
  const closers = c.useSelector(c.selectClosers())
  const setters = c.useSelector(c.selectSetters())

  const [regName, setRegName] = React.useState('')
  const [regEmail, setRegEmail] = React.useState('')
  const [regPassword, setRegPassword] = React.useState('')
  const [regRole, setRegRole] = React.useState<'closer' | 'setter'>('closer')
  const [regLoading, setRegLoading] = React.useState(false)
  const [regResult, setRegResult] = React.useState<{
    ok: boolean
    msg: string
  } | null>(null)

  const handleRegister = React.useCallback(async () => {
    const name = regName.trim()
    const email = regEmail.trim().toLowerCase()
    const password = regPassword

    if (!name || !email || !password) {
      setRegResult({ ok: false, msg: 'All fields required' })
      return
    }
    if (password.length < 6) {
      setRegResult({ ok: false, msg: 'Password must be at least 6 characters' })
      return
    }

    setRegLoading(true)
    setRegResult(null)

    const { uid, err } = await createUserAccount(email, password)

    if (err) {
      setRegLoading(false)
      setRegResult({ ok: false, msg: err })
      return
    }

    const item = { label: name, value: email }
    if (regRole === 'closer') {
      c.dispatch(c.addCloser(item))
    } else {
      c.dispatch(c.addSetter(item))
    }

    setRegLoading(false)
    setRegResult({
      ok: true,
      msg: `Account created: ${email} (${uid.slice(0, 8)}…)`,
    })
    setRegName('')
    setRegEmail('')
    setRegPassword('')
  }, [regName, regEmail, regPassword, regRole])

  return (
    <div className={styles['cs-wrapper']}>
      {/* Register Account */}
      <div className={styles['cs-register']}>
        <div className={styles['cs-register-header']}>
          <Icon name="account-plus-outline" size={20} />
          <span>Register New Account</span>
        </div>
        <div className={styles['cs-register-form']}>
          <input
            className={styles['cs-input']}
            onChange={(e) => setRegName(e.target.value)}
            placeholder="Full name"
            value={regName}
          />
          <input
            className={styles['cs-input']}
            onChange={(e) => setRegEmail(e.target.value)}
            placeholder="Email"
            type="email"
            value={regEmail}
          />
          <input
            className={styles['cs-input']}
            onChange={(e) => setRegPassword(e.target.value)}
            placeholder="Password (min 6 chars)"
            type="password"
            value={regPassword}
          />
          <select
            className={styles['cs-select']}
            onChange={(e) => setRegRole(e.target.value as 'closer' | 'setter')}
            value={regRole}
          >
            <option value="closer">Closer</option>
            <option value="setter">Setter</option>
          </select>
          <button
            className={styles['cs-register-btn']}
            disabled={regLoading}
            onClick={handleRegister}
          >
            {regLoading ? 'Creating...' : 'Create Account'}
          </button>
        </div>
        {regResult && (
          <div
            className={
              regResult.ok ? styles['cs-reg-success'] : styles['cs-reg-error']
            }
          >
            {regResult.msg}
          </div>
        )}
      </div>

      {/* Closers & Setters Lists */}
      <div className={styles['cs-container']}>
        <CloserSetterList
          items={closers}
          label="Closers"
          sublabel="homeRep dropdown options"
          add={(item) => c.dispatch(c.addCloser(item))}
          remove={(item) => c.dispatch(c.removeCloser(item))}
          update={(old, updated) =>
            c.dispatch(c.updateCloser({ old, updated }))
          }
        />
        <CloserSetterList
          items={setters}
          label="Setters"
          sublabel="solarRep dropdown options"
          add={(item) => c.dispatch(c.addSetter(item))}
          remove={(item) => c.dispatch(c.removeSetter(item))}
          update={(old, updated) =>
            c.dispatch(c.updateSetter({ old, updated }))
          }
        />
      </div>
    </div>
  )
})

interface CloserSetterListProps {
  readonly items: readonly c.CloserOrSetter[]
  readonly label: string
  readonly sublabel: string
  readonly add: (item: c.CloserOrSetter) => void
  readonly remove: (item: c.CloserOrSetter) => void
  readonly update: (old: c.CloserOrSetter, updated: c.CloserOrSetter) => void
}

const CloserSetterList = React.memo<CloserSetterListProps>(
  function CloserSetterList({ items, label, sublabel, add, remove, update }) {
    const [newLabel, setNewLabel] = React.useState('')
    const [editingIdx, setEditingIdx] = React.useState<number | null>(null)
    const [editLabel, setEditLabel] = React.useState('')
    const [editValue, setEditValue] = React.useState('')

    const handleAdd = React.useCallback(() => {
      const trimmedLabel = newLabel.trim()
      if (!trimmedLabel) return
      add({ label: trimmedLabel, value: trimmedLabel })
      setNewLabel('')
    }, [newLabel, add])

    const handleRemove = React.useCallback(
      (item: c.CloserOrSetter) => {
        if (!window.confirm(`Remove "${item.label}" from ${label}?`)) return
        remove(item)
      },
      [label, remove],
    )

    const handleEditStart = React.useCallback(
      (idx: number, item: c.CloserOrSetter) => {
        setEditingIdx(idx)
        setEditLabel(item.label)
        setEditValue(item.value)
      },
      [],
    )

    const handleEditSave = React.useCallback(
      (old: c.CloserOrSetter) => {
        const trimmedLabel = editLabel.trim()
        const trimmedValue = editValue.trim()
        if (!trimmedLabel || !trimmedValue) return
        if (trimmedLabel === old.label && trimmedValue === old.value) {
          setEditingIdx(null)
          return
        }
        update(old, { label: trimmedLabel, value: trimmedValue })
        setEditingIdx(null)
      },
      [editLabel, editValue, update],
    )

    const handleEditKeyDown = React.useCallback(
      (e: React.KeyboardEvent, old: c.CloserOrSetter) => {
        if (e.key === 'Enter') handleEditSave(old)
        if (e.key === 'Escape') setEditingIdx(null)
      },
      [handleEditSave],
    )

    return (
      <div className={styles['cs-section']}>
        <div className={styles['cs-header']}>
          <span className={styles['cs-title']}>{label}</span>
          <span className={styles['cs-subtitle']}>{sublabel}</span>
          <span className={styles['cs-count']}>{items.length}</span>
        </div>

        <div className={styles['cs-list']}>
          {items.map((item, idx) => {
            const isEditing = editingIdx === idx

            return (
              <div className={styles['cs-item']} key={`${item.value}-${idx}`}>
                {isEditing ? (
                  <div className={styles['cs-edit-row']}>
                    <input
                      autoFocus
                      className={styles['cs-input']}
                      onChange={(e) => setEditLabel(e.target.value)}
                      onKeyDown={(e) => handleEditKeyDown(e, item)}
                      placeholder="Label"
                      value={editLabel}
                    />
                    <input
                      className={styles['cs-input']}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => handleEditKeyDown(e, item)}
                      placeholder="Value"
                      value={editValue}
                    />
                    <button
                      className={styles['cs-save-btn']}
                      onClick={() => handleEditSave(item)}
                    >
                      <Icon color="white" name="check" size={16} />
                    </button>
                    <button
                      className={styles['cs-cancel-btn']}
                      onClick={() => setEditingIdx(null)}
                    >
                      <Icon color="white" name="close" size={16} />
                    </button>
                  </div>
                ) : (
                  <div className={styles['cs-item-row']}>
                    <span className={styles['cs-item-label']}>
                      {item.label}
                    </span>
                    {item.label !== item.value && (
                      <span className={styles['cs-item-value']}>
                        ({item.value})
                      </span>
                    )}
                    <div className={styles['cs-item-actions']}>
                      <div
                        className={styles['cs-icon-btn']}
                        onClick={() => handleEditStart(idx, item)}
                        title="Edit"
                      >
                        <Icon
                          color="rgba(128,128,128,0.6)"
                          name="pencil"
                          size={16}
                        />
                      </div>
                      <div
                        className={styles['cs-icon-btn']}
                        onClick={() => handleRemove(item)}
                        title="Remove"
                      >
                        <Icon
                          color="rgba(180,50,50,0.6)"
                          name="trash-can-outline"
                          size={16}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })}

          {items.length === 0 && (
            <div className={styles['cs-empty']}>
              No {label.toLowerCase()} yet
            </div>
          )}
        </div>

        <div className={styles['cs-add-row']}>
          <input
            className={styles['cs-input']}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAdd()
            }}
            placeholder={`Add new ${label.toLowerCase().slice(0, -1)}...`}
            value={newLabel}
          />
          <button className={styles['cs-add-btn']} onClick={handleAdd}>
            <Icon color="white" name="plus" size={18} />
            Add
          </button>
        </div>
      </div>
    )
  },
)

// #endregion CloserSetterTab

export const rootSurfaceAdminPanel: c.Surface = 'canvas'
